/**
 * Bootstrap Sequence for Multiplayer
 *
 * Manages initialization of Yjs documents with IndexedDB persistence
 * and WebSocket connection to PartyKit.
 *
 * Each project gets its own Y.Doc instance, cached for reuse.
 *
 * State machine:
 *   INIT -> LOADING -> READY (online) or LOCAL_READY (offline)
 *   READY <-> RECONNECTING (on connection loss/restore)
 *   Any -> ERROR (on unrecoverable failure)
 */

import * as Y from 'yjs'
import * as Sentry from '@sentry/nextjs'
import { IndexeddbPersistence } from 'y-indexeddb'
import {
  deleteProjectDatabase,
  installCorruptStoreDetection,
  wipeLocalStoreIfMarkedCorrupt,
} from './corruptStoreRecovery'
import YPartyServerProvider from 'y-partyserver/provider'
import { create } from 'zustand'
import { collabHost } from './collabHost'
import { captureSentryError } from '@/lib/sentry'
import { remapProjectIds, type DeepCopyIdMaps } from './remapProjectIds'
import type { Job, DraftSettings } from '@/types'
import type { Action, Workflow } from '@/types/shared'
import { isPendingMarker } from '@/lib/hooks/useBackgroundImageUpload'
import { destroyUndoManager } from '@/lib/undoManager'
import { EDITOR_VOLATILE_ORIGIN, LOCAL_ORIGIN, PER_USER_ORIGIN, SYSTEM_ORIGIN } from '@/lib/constants'
import { useProjectAccessStore } from '@/store/projectAccessStore'
import { getAccessToken } from '@/lib/auth'
import { getGhostSessionForProject } from '@/store/ghostModeStore'
import { setAssetProjectContext } from '@/lib/assetProjectContext'
import {
  attachReadOnlyOutboundGuard,
  isProviderDocWritesSuppressed,
  setProviderDocWritesSuppressed,
  shouldDiscardSuppressedCollabCache,
} from './readOnlyOutboundGuard'

export type BootstrapState =
  | 'init' // App starting
  | 'loading' // Local replica replaying, then the WebSocket sync
  | 'local_ready' // Loaded, but offline (no network)
  | 'ready' // Fully synced, online
  | 'reconnecting' // Was online, lost connection, attempting recovery
  | 'error' // Unrecoverable error

export type CollabProvider = YPartyServerProvider

export interface BootstrapEngine {
  state: BootstrapState
  error?: Error
  isOffline: boolean
  ydoc: Y.Doc
  provider: CollabProvider | null
  abort: () => void
}

/**
 * Zustand store for bootstrap state (reactive)
 */
/**
 * A server-initiated access change on the live collab socket (SEC-5). The
 * project page consumes and clears it: 'revoked' → leave the project (the
 * bouncer screen; reconnecting would just be denied again), 'downgraded' →
 * re-resolve the project role and re-enter bootstrap read-only.
 */
export type CollabAccessChange = 'revoked' | 'downgraded'

interface BootstrapStore {
  state: BootstrapState
  isOffline: boolean
  error: Error | null
  currentProjectId: string | null
  accessChange: CollabAccessChange | null
  /** The IndexedDB replica of the current project holds content — enough to render from. */
  localReplicaReady: boolean
  setState: (state: BootstrapState) => void
  setIsOffline: (offline: boolean) => void
  setError: (error: Error | null) => void
  setCurrentProjectId: (projectId: string | null) => void
  setAccessChange: (change: CollabAccessChange | null) => void
  setLocalReplicaReady: (ready: boolean) => void
}

export const useBootstrapStore = create<BootstrapStore>(set => ({
  state: 'init',
  isOffline: true,
  error: null,
  currentProjectId: null,
  accessChange: null,
  localReplicaReady: false,
  setState: state => set({ state }),
  setIsOffline: isOffline => set({ isOffline }),
  setError: error => set({ error }),
  setAccessChange: accessChange => set({ accessChange }),
  setCurrentProjectId: currentProjectId => {
    // Asset fetches carry the open project as their claimed context (SEC-4
    // legacy owner-less assets); api.ts cannot read this store directly.
    setAssetProjectContext(currentProjectId)
    set({ currentProjectId, localReplicaReady: false })
  },
  setLocalReplicaReady: localReplicaReady => set({ localReplicaReady }),
}))

// Track active bootstrap to prevent duplicates
let activeBootstrap: { projectId: string; abort: () => void } | null = null
// Serialize cache construction for one project. Concurrent callers used to
// observe empty caches before either async setup completed, creating competing
// persistence/provider instances and leaking one of them.
const projectBootstrapTails = new Map<string, Promise<unknown>>()

// Per-project caches - docs are kept alive to preserve data
const projectDocs = new Map<string, Y.Doc>()
const projectPersistence = new Map<string, IndexeddbPersistence>()
// Settles once the persistence in `projectPersistence` has replayed the local
// store into the doc (or gave up). `warmLocalReplica` starts that replay before
// bootstrap() runs, so bootstrap awaits this instead of the store directly.
const projectPersistenceSynced = new Map<string, Promise<void>>()
const projectProviders = new Map<string, CollabProvider>()

export const READ_ONLY_WRITE_GUARD_ENABLED_STORAGE_KEY = 'martini.readOnlyWriteGuard.enabled'
export const READ_ONLY_WRITE_GUARD_DISABLED_STORAGE_KEY = 'martini.readOnlyWriteGuard.disabled'

let readOnlyWriteGuardEnabledForTest: boolean | null = null

/**
 * Bootstrap options
 */
export interface BootstrapOptions {
  token?: string // JWT token for PartyKit authentication
  /**
   * When true, this session's PartyKit connection is read-only: outbound Yjs
   * document writes (updates + handshake SyncStep2 diffs) are suppressed.
   * Awareness is unchanged. Match PartyKit's `canWrite` (viewer / link-view /
   * preview non-owner), not the UI-only access-store role — snapshot preview
   * owners must still seed the room.
   *
   * A later writable boot must not lift suppression on the same cached
   * provider — the local doc may still hold viewer-era diffs. Bootstrap
   * discards that cache and starts a fresh session instead.
   */
  readOnly?: boolean
}

/**
 * Get or create a Y.Doc for a project
 */
function getOrCreateDoc(projectId: string): Y.Doc {
  let doc = projectDocs.get(projectId)
  if (!doc) {
    doc = new Y.Doc()
    projectDocs.set(projectId, doc)
    console.log(`[Bootstrap] Created new Y.Doc for project ${projectId}`)
  }
  return doc
}

/**
 * Get the Y.Doc for the current project
 * Throws if no project is bootstrapped
 */
export function getYdoc(): Y.Doc {
  const { currentProjectId } = useBootstrapStore.getState()
  if (!currentProjectId) {
    throw new Error('No project bootstrapped yet - call bootstrap() first')
  }
  const doc = projectDocs.get(currentProjectId)
  if (!doc) {
    throw new Error(`No Y.Doc found for project ${currentProjectId}`)
  }
  return doc
}

/**
 * Execute a function within a Yjs transaction with the specified origin
 * for undo/redo tracking
 */
export function yTransact<T>(fn: () => T, origin: symbol): T {
  const { currentProjectId } = useBootstrapStore.getState()
  const ghostSession = currentProjectId ? getGhostSessionForProject(currentProjectId) : null
  if (ghostSession && ghostSession.mode !== 'write' && origin !== SYSTEM_ORIGIN) {
    throw new Error('Ghost mode is read-only. Enable write mode to edit this project.')
  }

  const ydoc = getYdoc()
  let result: T
  ydoc.transact(() => {
    result = fn()
  }, origin)
  return result!
}

/**
 * Correctness chokepoint for synced writes.
 *
 * The write guard is default-off and can be enabled with
 * NEXT_PUBLIC_READ_ONLY_WRITE_GUARD_ENABLED=true or the localStorage enable key.
 * A localStorage kill switch always disables the guard for client-side recovery.
 */
export function guardedTransact<T>(fn: () => T, origin: symbol): T {
  const ydoc = getYdoc()
  const effectiveOrigin = getEffectiveTransactionOrigin(ydoc, origin)

  if (shouldBlockGuardedTransact(effectiveOrigin)) {
    recordBlockedReadOnlyWrite(effectiveOrigin)
    return undefined as T
  }

  return yTransact(fn, origin)
}

function shouldBlockGuardedTransact(origin: unknown): boolean {
  if (!isReadOnlyWriteGuardEnabled()) return false
  if (isReadOnlyAllowedOrigin(origin)) return false

  // Fail closed: a null role means access hasn't resolved yet (project
  // switch, initial load) — block user-origin writes until it has, the same
  // way the UI treats the unresolved window as view-only. Editors aren't
  // affected in practice: the page publishes the role before the canvas
  // becomes interactive, and system writebacks use allowed origins.
  const access = useProjectAccessStore.getState()
  return access.userRole === 'viewer' || access.userRole === null
}

function isReadOnlyAllowedOrigin(origin: unknown): boolean {
  return origin === SYSTEM_ORIGIN || origin === PER_USER_ORIGIN
}

function getEffectiveTransactionOrigin(ydoc: Y.Doc, origin: symbol): unknown {
  const activeTransaction = (ydoc as unknown as { _transaction?: { origin?: unknown } | null })._transaction
  return activeTransaction ? activeTransaction.origin : origin
}

function isReadOnlyWriteGuardEnabled(): boolean {
  if (readOnlyWriteGuardEnabledForTest !== null) return readOnlyWriteGuardEnabledForTest
  if (readLocalStorageFlag(READ_ONLY_WRITE_GUARD_DISABLED_STORAGE_KEY)) return false
  return (
    process.env.NEXT_PUBLIC_READ_ONLY_WRITE_GUARD_ENABLED === 'true' ||
    readLocalStorageFlag(READ_ONLY_WRITE_GUARD_ENABLED_STORAGE_KEY)
  )
}

function readLocalStorageFlag(key: string): boolean {
  if (typeof window === 'undefined') return false
  try {
    const value = window.localStorage.getItem(key)
    return value === 'true' || value === '1'
  } catch {
    return false
  }
}

function recordBlockedReadOnlyWrite(origin: unknown): void {
  const { currentProjectId, state } = useBootstrapStore.getState()
  const { userRole, visibility } = useProjectAccessStore.getState()

  Sentry.addBreadcrumb({
    category: 'read_only.write_guard',
    message: 'Blocked viewer Yjs transaction',
    level: 'warning',
    data: {
      projectId: currentProjectId,
      bootstrapState: state,
      userRole,
      visibility,
      origin: getTransactionOriginLabel(origin),
    },
  })
}

function getTransactionOriginLabel(origin: unknown): string {
  if (origin === LOCAL_ORIGIN) return 'local-undo'
  if (origin === SYSTEM_ORIGIN) return 'system-event'
  if (origin === PER_USER_ORIGIN) return 'per-user-event'
  if (origin === EDITOR_VOLATILE_ORIGIN) return 'editor-volatile-event'
  if (typeof origin === 'symbol') return origin.description ?? 'symbol'
  if (origin === null) return 'null'
  if (origin === undefined) return 'undefined'
  return typeof origin
}

function createCollabProviderOptions(token?: string, readOnly = false) {
  return {
    // Connect after the outbound write guard is installed so a read-only
    // handshake cannot race a non-empty SyncStep2 from IndexedDB.
    connect: false,
    // A viewer tab must not BroadcastChannel local mutations into a sibling
    // editor tab on the same machine, which would then persist them.
    disableBc: readOnly,
    params: async () => {
      const accessToken = (await getAccessToken()) ?? token
      return accessToken ? { token: accessToken } : {}
    },
  }
}

/**
 * Build the provider for a project room. y-partyserver re-resolves and awaits
 * the async `params` (the auth token) before every reconnect, so only the
 * unsuccessful-reconnect backoff patch is added on top.
 */
function createCollabProvider(args: {
  host: string
  projectId: string
  doc: Y.Doc
  token?: string
  readOnly?: boolean
}): CollabProvider {
  const options = createCollabProviderOptions(args.token, args.readOnly === true)
  const provider = new YPartyServerProvider(args.host, `project-${args.projectId}`, args.doc, options)
  attachProviderReconnectBackoff(provider)
  return provider
}

function configureProviderWriteAccess(provider: CollabProvider, readOnly: boolean): void {
  attachReadOnlyOutboundGuard(provider)
  if (readOnly) {
    setProviderDocWritesSuppressed(provider, true)
    provider.disableBc = true
    return
  }
  if (isProviderDocWritesSuppressed(provider)) {
    // Viewer-era local diffs may still be in this Y.Doc. Lifting suppression
    // would send them on the next SyncStep2. Keep the socket read-only; the
    // caller must discard this cache and boot a fresh writable session.
    provider.disableBc = true
    return
  }
  setProviderDocWritesSuppressed(provider, false)
  provider.disableBc = false
}

type CollabSessionDiscardListener = (projectId: string) => void
const collabSessionDiscardListeners = new Set<CollabSessionDiscardListener>()

/**
 * In-process hook for a discarded read-only collab cache. yjsStore also
 * listens for the `martini:collab-session-discarded` window event so map
 * observers unbind before the cached Y.Doc is destroyed.
 */
export function onCollabSessionDiscarded(listener: CollabSessionDiscardListener): () => void {
  collabSessionDiscardListeners.add(listener)
  return () => {
    collabSessionDiscardListeners.delete(listener)
  }
}

function notifyCollabSessionDiscarded(projectId: string): void {
  for (const listener of collabSessionDiscardListeners) {
    try {
      listener(projectId)
    } catch (err) {
      console.warn('[Bootstrap] collab session discard listener failed:', err)
    }
  }
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent('martini:collab-session-discarded', { detail: { projectId } }))
  }
}

/**
 * Disconnect + destroy a project's cached provider (only — the Y.Doc and
 * IndexedDB persistence stay alive). Used when the session's provider must be
 * rebuilt: read-only cache discard, access change.
 */
function teardownProvider(projectId: string): void {
  const provider = projectProviders.get(projectId)
  if (provider) {
    try {
      provider.disconnect()
    } catch {
      // Already disconnected.
    }
    try {
      provider.destroy()
    } catch {
      // Already destroyed.
    }
    projectProviders.delete(projectId)
  }
}

async function closePersistence(projectId: string): Promise<void> {
  const persistence = projectPersistence.get(projectId)
  if (!persistence) return
  try {
    await Promise.race([persistence.destroy(), new Promise<void>(resolve => setTimeout(resolve, 2000))])
  } catch {
    // Already closed or never opened.
  }
  projectPersistence.delete(projectId)
  projectPersistenceSynced.delete(projectId)
}

/** How long the local replica may take to replay before boot proceeds without it. */
const LOCAL_REPLICA_SYNC_TIMEOUT_MS = 3000

/**
 * Open the project's IndexedDB store and replay it into `ydoc`. Registers the
 * persistence and its settle promise in the per-project caches, so a later
 * `bootstrapOnce` reuses both instead of opening the store a second time.
 *
 * Never rejects. The local store is a cache of the server doc: if opening it
 * throws, the failure is reported and the cache entries are dropped so the
 * boot (and any retry) continues straight to the server sync rather than
 * replaying a poisoned promise — the same outcome the persistence 'error'
 * event already had.
 *
 * Caller guarantees no persistence exists for `projectId` yet.
 */
function openLocalReplica(projectId: string, ydoc: Y.Doc): Promise<void> {
  const synced = (async () => {
    // Repair first: if a previous session detected a corrupt local store
    // (replay failures), wipe it now — before the persistence opens it —
    // so this boot starts from a fresh cache and the server doc.
    await wipeLocalStoreIfMarkedCorrupt(projectId)

    const persistence = new IndexeddbPersistence(`martini-${projectId}`, ydoc)
    projectPersistence.set(projectId, persistence)

    // Detect corrupt-replay failures for the NEXT boot's repair. The
    // window listeners are scoped to the sync window (+ grace) below.
    const removeCorruptDetection = installCorruptStoreDetection(projectId, persistence)

    await new Promise<void>(resolve => {
      const onSynced = () => {
        console.log(`[Bootstrap] IndexedDB synced for project ${projectId}`)
        persistence.off('synced', onSynced)
        clearTimeout(idbTimeout)
        resolve()
      }
      persistence.on('synced', onSynced)

      persistence.on('error', (err: Error) => {
        console.warn('[Bootstrap] IndexedDB error:', err)
        clearTimeout(idbTimeout)
        resolve()
      })

      const idbTimeout = setTimeout(() => {
        console.warn('[Bootstrap] IndexedDB timeout')
        resolve()
      }, LOCAL_REPLICA_SYNC_TIMEOUT_MS)
    })

    // Corrupt replays surface as uncaught errors during (or shortly
    // after) the initial sync — keep the scoped listeners up briefly
    // past the sync barrier, then remove them.
    setTimeout(removeCorruptDetection, 5000)
  })().catch((error: unknown) => {
    console.warn(
      `[Bootstrap] Local replica unavailable for project ${projectId}; continuing with the server doc`,
      error,
    )
    captureSentryError(error, {
      operation: 'bootstrap_local_replica_open',
      level: 'warning',
      extra: { projectId },
    })
    projectPersistence.delete(projectId)
    projectPersistenceSynced.delete(projectId)
  })
  projectPersistenceSynced.set(projectId, synced)
  return synced
}

/**
 * True when this browser already holds a local replica for the project. Uses
 * `indexedDB.databases()` so the check never creates an empty store for a
 * project this visitor may turn out not to have access to.
 */
async function hasLocalReplica(projectId: string): Promise<boolean> {
  if (typeof indexedDB === 'undefined' || typeof indexedDB.databases !== 'function') return false
  try {
    const databases = await indexedDB.databases()
    return databases.some(db => db.name === `martini-${projectId}`)
  } catch (error) {
    // Some private-browsing modes refuse IndexedDB outright. Nothing to warm
    // then; bootstrap() opens the store itself later and handles the same refusal.
    captureSentryError(error, {
      operation: 'bootstrap_local_replica_probe',
      level: 'warning',
      extra: { projectId },
    })
    return false
  }
}

/**
 * Start replaying the project's local replica before auth and access have
 * resolved. Reading the store needs no credentials — it is this browser's own
 * cache from an earlier open — so it can overlap the sign-in and access round
 * trips instead of following them. When `bootstrap()` later runs for the same
 * project it finds the persistence in place and awaits the same replay, so a
 * returning visitor sees the editor as soon as access resolves rather than
 * after IndexedDB + the server sync.
 *
 * A no-op when the project has no replica here (first open) or a persistence
 * already exists. Never touches `useBootstrapStore`: nothing observable
 * changes until bootstrap() decides what to show.
 */
export async function warmLocalReplica(projectId: string): Promise<void> {
  if (typeof window === 'undefined') return
  if (localReplicaReplay(projectId)) return
  if (!(await hasLocalReplica(projectId))) return
  // The existence check yielded; a bootstrap() that started meanwhile owns the store.
  if (localReplicaReplay(projectId)) return
  console.log(`[Bootstrap] Warming local replica for project ${projectId}`)
  await openLocalReplica(projectId, getOrCreateDoc(projectId))
}

/**
 * The in-flight or settled replay of the project's local store, if a
 * persistence exists (or is being opened) for it. A persistence seeded without
 * a replay promise counts as already settled.
 */
function localReplicaReplay(projectId: string): Promise<void> | null {
  return projectPersistenceSynced.get(projectId) ?? (projectPersistence.has(projectId) ? Promise.resolve() : null)
}

/**
 * Drop a read-only collab cache so a writable boot cannot handshake viewer-era
 * local diffs. Closes IndexedDB first; only destroys the in-memory doc and
 * provider after the local store is actually gone. Returns false when another
 * tab still holds the database — caller must keep outbound suppression.
 */
export async function discardSuppressedCollabCache(projectId: string): Promise<boolean> {
  await closePersistence(projectId)
  const wiped = typeof window === 'undefined' ? true : await deleteProjectDatabase(projectId)
  if (!wiped) return false

  destroyUndoManager()
  notifyCollabSessionDiscarded(projectId)

  teardownProvider(projectId)

  const doc = projectDocs.get(projectId)
  if (doc) {
    try {
      doc.destroy()
    } catch {
      // Already destroyed.
    }
    projectDocs.delete(projectId)
  }

  return true
}

/**
 * Make the reconnect backoff actually count unsuccessful attempts.
 *
 * y-websocket resets its backoff in the socket's `open` handler, so a server
 * that accepts the upgrade and *then* closes us — the collab server rejecting
 * auth with 1008 after the handshake — reads as a successful connect and is
 * retried every ~100ms forever. Treat a close that never synced as an
 * unsuccessful attempt so the exponential backoff actually applies (it caps at
 * the provider's maxBackoffTime, so the client still recovers on its own once
 * a refreshed token is accepted). The provider reads this counter
 * synchronously just after this listener returns.
 *
 * A healthy session is untouched: it synced, so the counter resets and a
 * network blip still reconnects immediately.
 */
export function attachProviderReconnectBackoff(
  provider: Pick<CollabProvider, 'on' | 'synced' | 'wsUnsuccessfulReconnects'>,
): void {
  // Consecutive closes that never reached a sync. Tracked here rather than read
  // off the provider because the provider zeroes its own counter on socket
  // `open`, which is exactly the case we need to keep counting through.
  let unsyncedCloses = 0

  provider.on('connection-close', () => {
    unsyncedCloses = provider.synced ? 0 : unsyncedCloses + 1
    provider.wsUnsuccessfulReconnects = unsyncedCloses
  })
}

export function __setReadOnlyWriteGuardEnabledForTest(enabled: boolean | null): void {
  readOnlyWriteGuardEnabledForTest = enabled
}

export function __setYdocForTest(projectId: string, ydoc: Y.Doc): void {
  projectDocs.set(projectId, ydoc)
  useBootstrapStore.getState().setCurrentProjectId(projectId)
}

export function __seedCachedCollabSessionForTest(
  projectId: string,
  session: {
    doc: Y.Doc
    provider: CollabProvider
    persistence?: IndexeddbPersistence
  },
): void {
  projectDocs.set(projectId, session.doc)
  projectProviders.set(projectId, session.provider)
  if (session.persistence) projectPersistence.set(projectId, session.persistence)
  useBootstrapStore.getState().setCurrentProjectId(projectId)
}

export function __getCachedYdocForTest(projectId: string): Y.Doc | undefined {
  return projectDocs.get(projectId)
}

export function __getCachedProviderForTest(projectId: string): CollabProvider | undefined {
  return projectProviders.get(projectId)
}

export function __clearYdocsForTest(): void {
  projectDocs.clear()
  projectPersistence.clear()
  projectPersistenceSynced.clear()
  projectProviders.clear()
  projectBootstrapTails.clear()
  activeBootstrap = null
  const store = useBootstrapStore.getState()
  store.setState('init')
  store.setIsOffline(true)
  store.setError(null)
  store.setCurrentProjectId(null)
}

/**
 * Get the WebSocket provider for the current project
 */
export function getProvider(): CollabProvider | null {
  const { currentProjectId } = useBootstrapStore.getState()
  if (!currentProjectId) return null
  return projectProviders.get(currentProjectId) || null
}

/**
 * Close code + reasons the collab server emits when a live socket's
 * access changes (party/src/revalidate.ts + auth.ts). Without handling them
 * the provider would reconnect every few seconds forever: the connect path
 * denies a revoked user with the same 1008, and a demoted user would be
 * re-admitted read-only while still editing as if writable.
 */
const ACCESS_CLOSE_CODE = 1008
const ACCESS_REVOKED_REASON = 'Project access revoked'
const ACCESS_DOWNGRADED_REASON = 'Project access downgraded'
/** The connect-time denial, when the backend answered 403 (not 401: an expired token must keep retrying). */
const ACCESS_DENIED_AT_CONNECT_REASON = 'Backend denied access (403)'

function classifyAccessClose(event: { code?: number; reason?: string } | null | undefined): CollabAccessChange | null {
  if (!event || event.code !== ACCESS_CLOSE_CODE) return null
  if (event.reason === ACCESS_DOWNGRADED_REASON) return 'downgraded'
  if (event.reason === ACCESS_REVOKED_REASON || event.reason === ACCESS_DENIED_AT_CONNECT_REASON) return 'revoked'
  return null
}

/**
 * Stop the provider's reconnect loop on an access close and hand the change
 * to the project page (via the bootstrap store). The provider is torn down
 * here so no further attempt fires; the doc + IndexedDB stay alive, and the
 * page decides what happens next (bouncer vs. read-only re-boot).
 */
function attachAccessChangeHandoff(provider: CollabProvider, projectId: string): void {
  provider.on('connection-close', ((event: { code?: number; reason?: string } | null | undefined) => {
    const change = classifyAccessClose(event)
    if (!change) return
    // A stale close from an already-replaced provider must not tear down its
    // successor.
    if (projectProviders.get(projectId) !== provider) return
    console.warn(`[Bootstrap] Collab access ${change} for project ${projectId} (${event?.reason})`)
    Sentry.addBreadcrumb({
      category: 'bootstrap.access',
      message: `Collab access ${change}`,
      level: 'warning',
      data: { projectId, reason: event?.reason },
    })
    teardownProvider(projectId)
    notifyCollabSessionDiscarded(projectId)
    if (useBootstrapStore.getState().currentProjectId === projectId) {
      useBootstrapStore.getState().setAccessChange(change)
    }
  }) as (...args: unknown[]) => void)
}

/**
 * Bootstrap the multiplayer system for a project
 *
 * Each project gets its own Y.Doc, IndexedDB persistence, and WebSocket provider.
 * Documents are cached so switching back to a project is instant.
 */
export function bootstrap(projectId: string, options?: BootstrapOptions): Promise<BootstrapEngine> {
  const previous = projectBootstrapTails.get(projectId)
  const run = () => bootstrapOnce(projectId, options)
  const next = previous ? previous.then(run, run) : run()
  projectBootstrapTails.set(projectId, next)

  const clearTail = () => {
    if (projectBootstrapTails.get(projectId) === next) projectBootstrapTails.delete(projectId)
  }
  void next.then(clearTail, clearTail)
  return next
}

async function bootstrapOnce(projectId: string, options?: BootstrapOptions): Promise<BootstrapEngine> {
  const store = useBootstrapStore.getState()

  // Cleanup any existing bootstrap (e.g., React strict mode)
  if (activeBootstrap && activeBootstrap.projectId !== projectId) {
    activeBootstrap.abort()
    activeBootstrap = null
  }

  let bootOptions = options
  const cachedProvider = projectProviders.get(projectId)
  if (shouldDiscardSuppressedCollabCache(cachedProvider, bootOptions?.readOnly === true)) {
    console.log(`[Bootstrap] Discarding read-only collab cache for ${projectId} before writable connect`)
    const discarded = await discardSuppressedCollabCache(projectId)
    if (!discarded) {
      console.warn(
        `[Bootstrap] Keeping outbound write suppression for ${projectId}; local store still holds read-only diffs`,
      )
      bootOptions = { ...bootOptions, readOnly: true }
    }
  }

  // Resolve the collab host BEFORE touching caches, so a deployment without
  // one rejects here instead of surfacing as a generic connect failure.
  const host = collabHost()

  // Get or create the doc for this project
  const ydoc = getOrCreateDoc(projectId)

  // A replay already running or settled for this project (an earlier boot, or
  // `warmLocalReplica` started while auth/access were still resolving).
  const existingReplica = localReplicaReplay(projectId)

  // Check if we already have a provider for this project
  let provider = projectProviders.get(projectId)
  const alreadyHasProvider = !!provider

  // Update current project
  store.setCurrentProjectId(projectId)
  store.setState('init')
  store.setError(null)

  let aborted = false

  const engine: BootstrapEngine = {
    state: 'init',
    isOffline: true,
    ydoc,
    provider: provider || null,
    abort: () => {
      aborted = true
      // Don't destroy on abort - just mark as aborted
      // The doc/persistence/provider stay alive for potential reuse
      store.setState('init')
    },
  }

  activeBootstrap = { projectId, abort: engine.abort }

  // Update reactive state
  const updateState = (state: BootstrapState, isOffline: boolean, error?: Error) => {
    if (aborted) return
    engine.state = state
    engine.isOffline = isOffline
    engine.error = error
    store.setState(state)
    store.setIsOffline(isOffline)
    store.setError(error || null)
  }

  try {
    updateState('loading', true)

    // Step 1: Set up IndexedDB persistence (if not already) and wait for the
    // local replica to replay into the doc.
    if (existingReplica) {
      console.log(`[Bootstrap] Reusing existing IndexedDB persistence for project ${projectId}`)
      await existingReplica
    } else {
      await openLocalReplica(projectId, ydoc)
    }

    if (aborted) return engine

    // A populated local replica is enough to render from. The page shows the editor on
    // this signal while the server diff is still in flight; `state` stays 'loading' until
    // the socket syncs, so nothing else changes meaning.
    if (ydoc.getMap('scenes').size > 0 || ydoc.getMap('meta').size > 0) {
      store.setLocalReplicaReady(true)
    }

    // Step 2: Set up WebSocket provider (if not already)
    if (!alreadyHasProvider) {
      provider = createCollabProvider({
        host,
        projectId,
        doc: ydoc,
        token: bootOptions?.token,
        readOnly: bootOptions?.readOnly === true,
      })
      configureProviderWriteAccess(provider, bootOptions?.readOnly === true)
      attachAccessChangeHandoff(provider, projectId)
      projectProviders.set(projectId, provider)
      engine.provider = provider

      const connectionResult = await new Promise<'synced' | 'offline'>(resolve => {
        // A settled wait must not log a spurious "timeout" seconds later.
        const wsTimeout = setTimeout(() => {
          console.warn('[Bootstrap] WebSocket timeout')
          resolve('offline')
        }, 5000)
        provider!.on('sync', (synced: boolean) => {
          if (synced) {
            console.log(`[Bootstrap] WebSocket synced for project ${projectId}`)
            clearTimeout(wsTimeout)
            resolve('synced')
          }
        })
        provider!.on('status', ({ status }: { status: string }) => {
          if (status === 'disconnected') {
            clearTimeout(wsTimeout)
            resolve('offline')
          }
        })
        // connect() is async and rejects when the params fn throws; treat that
        // like any failed socket connect.
        provider!.connect().catch(err => {
          console.warn('[Bootstrap] WebSocket connect failed:', err)
          resolve('offline')
        })
      })

      if (connectionResult === 'synced') {
        // Record post-sync doc state for partial sync diagnosis
        const scenesSize = ydoc.getMap('scenes').size
        const metaSize = ydoc.getMap('meta').size
        Sentry.addBreadcrumb({
          category: 'bootstrap.sync',
          message: `WebSocket synced for project ${projectId}`,
          level: 'info',
          data: { projectId, scenesSize, metaSize },
        })
        if (metaSize > 0 && scenesSize === 0) {
          Sentry.addBreadcrumb({
            category: 'bootstrap.sync',
            message: 'Possible partial sync: meta populated but scenes empty',
            level: 'warning',
            data: { projectId, scenesSize, metaSize },
          })
        }
        updateState('ready', false)
      } else {
        updateState('local_ready', true)
      }

      // Handle ongoing connection state changes
      provider.on('status', ({ status }: { status: string }) => {
        if (aborted) return
        const currentState = useBootstrapStore.getState().state
        if (status === 'disconnected' && currentState === 'ready') {
          console.log('[Bootstrap] Connection lost, reconnecting...')
          updateState('reconnecting', true)
        }
      })

      provider.on('sync', (synced: boolean) => {
        if (aborted) return
        const currentState = useBootstrapStore.getState().state
        if (synced && (currentState === 'reconnecting' || currentState === 'local_ready')) {
          console.log('[Bootstrap] Connection restored')
          updateState('ready', false)
        }
      })
    } else {
      console.log(`[Bootstrap] Reusing existing WebSocket provider for project ${projectId}`)
      configureProviderWriteAccess(provider!, bootOptions?.readOnly === true)
      engine.provider = provider!

      // Check current connection status
      if (provider!.synced) {
        updateState('ready', false)
      } else {
        updateState('local_ready', true)
      }
    }

    console.log(`[Bootstrap] Complete for project ${projectId}`)
  } catch (err) {
    console.error('[Bootstrap] Error:', err)
    updateState('error', true, err instanceof Error ? err : new Error(String(err)))
  }

  return engine
}

/**
 * Get Yjs document cache statistics
 */
export function getYjsDocumentStats(): { cachedProjects: number; totalSizeBytes: number } {
  let totalSizeBytes = 0
  try {
    // Calculate size of all cached documents
    for (const doc of projectDocs.values()) {
      const stateVector = Y.encodeStateAsUpdate(doc)
      totalSizeBytes += stateVector.byteLength
    }
  } catch {
    // Ignore errors calculating size
  }
  return {
    cachedProjects: projectDocs.size,
    totalSizeBytes,
  }
}

/**
 * Cleanup bootstrap (call on unmount)
 * Note: This only marks as aborted, doesn't destroy docs/persistence
 */
export function cleanupBootstrap(): void {
  if (activeBootstrap) {
    activeBootstrap.abort()
    activeBootstrap = null
  }
  // Destroy UndoManager to clear undo stack on project switch
  destroyUndoManager()
}

/**
 * Reconnect provider after auth upgrade.
 * Use this after account upgrade to refresh the connection with new credentials.
 *
 * Performs a clean cutover: disconnect then reconnect.
 */
export function reconnectProvider(projectId: string): void {
  const provider = projectProviders.get(projectId)
  if (provider) {
    provider.disconnect()
    Promise.resolve(provider.connect()).catch(err => {
      console.warn(`[Bootstrap] Post-auth-upgrade reconnect failed for project ${projectId}:`, err)
    })
    console.log(`[Bootstrap] Provider reconnected for project ${projectId} (post-auth-upgrade)`)
  }
}

/**
 * Bootstrap a project and capture its Yjs state.
 * Used as source for project duplication.
 * Uses a temporary Y.Doc (not cached) to ensure we get fresh state from server.
 */
export async function bootstrapAndCapture(projectId: string): Promise<Uint8Array> {
  // Create a temporary doc (NOT cached) to ensure we get fresh state from the server
  // This avoids issues where a cached doc might have stale or partial data
  const tempDoc = new Y.Doc()

  const provider = createCollabProvider({ host: collabHost(), projectId, doc: tempDoc })

  try {
    await new Promise<void>((resolve, reject) => {
      if (provider.synced) {
        resolve()
        return
      }
      provider.on('sync', (synced: boolean) => {
        if (synced) resolve()
      })
      setTimeout(() => reject(new Error(`Bootstrap timeout: Failed to sync source project ${projectId}`)), 10000)
      Promise.resolve(provider.connect()).catch(reject)
    })

    // Capture state
    return Y.encodeStateAsUpdate(tempDoc)
  } finally {
    // Fully destroy the temporary provider and doc to prevent memory leaks
    provider.disconnect()
    provider.destroy()
    tempDoc.destroy()
  }
}

/**
 * Bootstrap a new project with initial state for duplication.
 * Uses a temporary Y.Doc (not cached) to avoid conflicts with normal bootstrap.
 */
export async function bootstrapForDuplication(
  projectId: string,
  initialState: Uint8Array,
  idMaps?: DeepCopyIdMaps,
): Promise<void> {
  // Create a temporary doc (NOT cached) to avoid conflicts with normal bootstrap
  // When the user opens this project later, they'll get a fresh doc that syncs from the server
  const tempDoc = new Y.Doc()

  Y.applyUpdate(tempDoc, initialState)

  // Sanitize transient state that won't resolve in the duplicate:

  // 1. Strip all jobs — they belong to the source project's generation
  //    pipeline and won't receive completion callbacks in the duplicate.
  const yJobs = tempDoc.getMap<Job>('jobs')
  for (const id of Array.from(yJobs.keys())) {
    yJobs.delete(id)
  }

  // 1b. Strip in-flight upscale jobs for the same reason — they reference the
  //     source project's assets/job ids and would otherwise render as permanent
  //     phantom "Upscaling…" version members in the duplicate.
  const yUpscaleJobs = tempDoc.getMap('upscaleJobs')
  for (const id of Array.from(yUpscaleJobs.keys())) {
    yUpscaleJobs.delete(id)
  }

  // 1c. Strip comments — they belong to the source project (DB rows point at it) and must not ride into duplicates.
  const yComments = tempDoc.getMap('comments')
  for (const id of Array.from(yComments.keys())) {
    yComments.delete(id)
  }

  // 1d. Run statuses point at source-project Supabase rows. Action authoring
  //     data stays (machinery duplicates), but execution state starts clean.
  const yWorkflowRunStatuses = tempDoc.getMap('workflowRunStatuses')
  for (const id of Array.from(yWorkflowRunStatuses.keys())) {
    yWorkflowRunStatuses.delete(id)
  }

  // Placed workflow IDs also identify globally unique Postgres rows. Keeping
  // a source ID would attach the copy's runs/saves to the original workflow.
  // Its immutable tool bundle and saved-workflow lineage can travel unchanged.
  const yWorkflows = tempDoc.getMap<Workflow>('workflows')
  const workflowIds = new Map<string, string>()
  for (const [id, workflow] of Array.from(yWorkflows.entries())) {
    const newId = crypto.randomUUID()
    const toolBundle = idMaps?.workflowToolBundles?.[id] ?? workflow.toolBundle
    workflowIds.set(id, newId)
    yWorkflows.delete(id)
    yWorkflows.set(newId, { ...workflow, id: newId, ...(toolBundle ? { toolBundle } : {}) })
  }
  const yActions = tempDoc.getMap<Action>('actions')
  for (const [id, action] of yActions) {
    const workflowId = action.workflowId && workflowIds.get(action.workflowId)
    if (workflowId) yActions.set(id, { ...action, workflowId })
  }

  // 2. Strip pending:<uuid> upload markers from draftSettings — the upload
  //    promises only exist in the original client's memory and will never
  //    resolve in the duplicate, causing permanent loading skeletons.
  const yDraftSettings = tempDoc.getMap<DraftSettings>('draftSettings')
  for (const [assetId, settings] of yDraftSettings) {
    const startPending = isPendingMarker(settings.startFrameUrl)
    const endPending = isPendingMarker(settings.endFrameUrl)
    const middlePending = isPendingMarker(settings.middleFrameUrl)
    const refsPending = settings.referenceImages?.some(u => isPendingMarker(u))

    if (startPending || endPending || middlePending || refsPending) {
      const keepIndices = settings.referenceImages?.map((u, i) => (!isPendingMarker(u) ? i : -1)).filter(i => i !== -1)

      yDraftSettings.set(assetId, {
        ...settings,
        startFrameUrl: startPending ? undefined : settings.startFrameUrl,
        endFrameUrl: endPending ? undefined : settings.endFrameUrl,
        middleFrameUrl: middlePending ? undefined : settings.middleFrameUrl,
        middleFrameTimeSec: middlePending ? undefined : settings.middleFrameTimeSec,
        referenceImages: keepIndices ? keepIndices.map(i => settings.referenceImages![i]) : settings.referenceImages,
        referenceImageNames: keepIndices
          ? keepIndices.map(i => settings.referenceImageNames?.[i] ?? null)
          : settings.referenceImageNames,
      })
    }
  }

  // 3. Remap set/character/set-asset id references so the copy points at its
  //    OWN freshly-minted Postgres rows, never the source's (deep-copy
  //    primitive, spec/46). idMaps come from POST /projects/:id/deep-copy-rows.
  if (idMaps) {
    remapProjectIds(tempDoc, idMaps)
  }

  const provider = createCollabProvider({ host: collabHost(), projectId, doc: tempDoc })

  try {
    await new Promise<void>((resolve, reject) => {
      if (provider.synced) {
        resolve()
        return
      }
      provider.on('sync', (synced: boolean) => {
        if (synced) resolve()
      })
      setTimeout(() => reject(new Error(`Bootstrap timeout: Failed to sync new project ${projectId}`)), 10000)
      Promise.resolve(provider.connect()).catch(reject)
    })

    // Wait after sync to ensure server has time to persist to durable storage.
    // The 'synced' event fires when client/server sync, but y-partykit's
    // storage.storeUpdate() is async and not awaited. We need to give the
    // server time to complete its async write operations before disconnecting.
    await new Promise(resolve => setTimeout(resolve, 2000))
  } finally {
    provider.disconnect()
    provider.destroy()
    tempDoc.destroy()
  }
}
